import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import { findValueInRecord } from '@salescore/buff-common'
import type { ViewFieldsFragment } from '@salescore/client-api'
import type { SheetCell, ViewQueryRecordNode } from '@salescore/core'
import { type ReactElement, useState } from 'react'
import { z } from 'zod'

import type { PivotColumn, PivotRowColumn, PivotSystemColumn, RSheetRecordNode } from '../../../domain/column.ts'
import { useCursorMutation } from '../../../recoil/cursor.ts'
import type { RowConfig } from '../../../recoil/rowConfig.ts'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms.ts'
import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'
import { SheetThreadsFormPopoverIfAllowed } from '../../form/SheetThreadsFormPopoverIfAllowed.tsx'

export const KpiPivotSystemCell = ({
  sheetCell,
  rootRecordNode,
  column,
  rowIndex,
  expandable,
  expanded,
  rowHeight,
  columns,
  setRowConfig,
  view,
  ability,
}: {
  sheetCell: SheetCell
  rootRecordNode: RSheetRecordNode
  column: PivotSystemColumn<ViewQueryRecordNode>
  rowIndex: number
  expandable: boolean
  expanded: boolean
  rowHeight: number
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  setRowConfig: (id: string, updater: (oldValue: RowConfig) => RowConfig) => void
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { height } = sheetCell

  const { width } = column

  const { selectRowCell } = useCursorMutation(ability)
  const [isCommentActive, setIsCommentActive] = useState(false) // 行にコメント入力中かの判定

  const nameParsed = z.string().safeParse(findValueInRecord(rootRecordNode.attributes, (k) => k.endsWith('_name')))
  return (
    <div
      style={{
        width,
        height: height * rowHeight,
        position: 'relative',
        overflow: 'hidden',
        transition: RSheetsStyle.cellTransition,
        border: `1px ${RSheetsStyle.color.border} solid`,
        fontSize: 10,
        textAlign: 'center',
        paddingTop: 15,
        color: '#aaa',
      }}
      onClick={() => {
        // コメント機能の操作中（アイコンへのホバーまたはPopover表示中）はセルの選択を行わない
        if (isCommentActive) return
        selectRowCell({ rowIndex, rootRecordNode, columns })
      }}
    >
      <SheetThreadsFormPopoverIfAllowed
        setIsCommentActive={setIsCommentActive}
        recordId={rootRecordNode.id}
        recordName={nameParsed.success ? nameParsed.data : undefined}
        view={view}
      >
        <div
          style={{
            fontSize: 10,
            textAlign: 'center',
            color: '#aaa',
          }}
        >
          {rowIndex + 1}
        </div>
      </SheetThreadsFormPopoverIfAllowed>
      {expandable && (
        <div
          className="absolute inset-x-0 bottom-0 cursor-pointer"
          onClick={() => {
            setRowConfig(rootRecordNode.id ?? '', (x) => ({ ...x, expanded: !expanded }))
          }}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </div>
      )}
    </div>
  )
}
